import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../Api';

const DIAS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

// aceita 'AAAA-MM-DD', 'DD/MM/AAAA' ou (ano, mês, dia)
const paraData = (a, m, d) => {
    if (m !== undefined) return new Date(Number(a), Number(m) - 1, Number(d));
    const s = String(a || '');
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) { const [y, mm, dd] = s.slice(0, 10).split('-').map(Number); return new Date(y, mm - 1, dd); }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) { const [dd, mm, y] = s.split('/').map(Number); return new Date(y, mm - 1, dd); }
    return new Date(NaN);
};
const dataBR = (dt) => `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;

/**
 * Dias da semana em que o professor pode lançar aula/frequência na turma (grade horária).
 * Turma sem grade → tudo liberado (como antes). Sábado/domingo letivo que não está na grade
 * também fica liberado. Em caso de erro, não trava o professor.
 *
 * permitido(data) / mensagem(data): data em 'AAAA-MM-DD', 'DD/MM/AAAA' ou (ano, mês, dia).
 */
export function useDiasDaGrade(id_class, id_teacher) {
    const [info, setInfo] = useState({ carregado: false, temGrade: false, dias: [], diasDaGrade: [], papel: '' });

    useEffect(() => {
        let ativo = true;
        if (!id_class) { setInfo({ carregado: true, temGrade: false, dias: [], diasDaGrade: [], papel: '' }); return undefined; }
        api.post('/schedule/days', { id_class, id_teacher })
            .then((r) => { if (ativo) setInfo({ carregado: true, temGrade: !!r?.data?.temGrade, dias: r?.data?.dias || [], diasDaGrade: r?.data?.diasDaGrade || [], papel: r?.data?.papel || '' }); })
            .catch(() => { if (ativo) setInfo({ carregado: true, temGrade: false, dias: [], diasDaGrade: [], papel: '' }); });
        return () => { ativo = false; };
    }, [id_class, id_teacher]);

    const liberados = useMemo(() => new Set(info.dias.map(Number)), [info.dias]);

    const permitido = useCallback((a, m, d) => {
        if (!info.temGrade) return true;
        const dt = paraData(a, m, d);
        if (isNaN(dt.getTime())) return true;
        return liberados.has(dt.getDay());
    }, [info.temGrade, liberados]);

    // "seg, qua e sex"
    const textoDias = useMemo(() => {
        const nomes = [...info.diasDaGrade].map(Number).sort((x, y) => x - y).map((d) => CURTO[d]);
        if (!nomes.length) return '';
        return nomes.length === 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
    }, [info.diasDaGrade]);

    const mensagem = useCallback((a, m, d) => {
        const dt = paraData(a, m, d);
        if (isNaN(dt.getTime())) return '';
        const quem = info.papel === 'ED_FISICA' ? 'de Educação Física' : 'do regente';
        return `No horário desta turma não há aula ${quem} na ${DIAS[dt.getDay()]} (${dataBR(dt)}). ` +
            (textoDias ? `Escolha um dia da grade: ${textoDias}` : 'Escolha outro dia') +
            ' (ou um sábado letivo). Se o horário estiver errado, fale com a direção.';
    }, [info.papel, textoDias]);

    return { ...info, permitido, mensagem, textoDias };
}

/** Faixa informando os dias de aula do professor nesta turma (só aparece se a turma tem grade). */
export function AvisoDiasDaGrade({ grade, style }) {
    if (!grade?.carregado || !grade.temGrade) return null;
    return (
        <div style={{
            background: '#e8f0fe', border: '1px solid #2563eb', color: '#1e3a8a', borderRadius: 10,
            padding: '8px 12px', fontSize: 13.5, margin: '8px 0', ...style,
        }} className="no-print">
            🕒 Nesta turma você lança aulas e frequências nos dias da grade horária:{' '}
            <strong>{grade.textoDias || '—'}</strong>{' '}e nos sábados letivos do calendário.
        </div>
    );
}
